import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react'
import { Link } from 'react-router'
import styles from './MastheadTab.module.css'

interface BaseProps {
  /** The current page or selected filter: underlined in the accent. */
  active?: boolean
  className?: string
  children: ReactNode
}

export type MastheadTabProps = BaseProps & (
  | { to: string; ref?: Ref<HTMLAnchorElement> }
  | ({ to?: undefined; ref?: Ref<HTMLButtonElement> } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'>)
)

/**
 * A plain-text tab for a PageMasthead: a link to another view (`to`, marked
 * aria-current) or a button that filters this one (pass aria-pressed).
 * Colored by --news-accent / --news-accent-light, so org pages use their brand.
 */
export function MastheadTab({ active, className, children, ...rest }: MastheadTabProps) {
  const classes = [styles.tab, active ? styles.active : '', className].filter(Boolean).join(' ')

  if (rest.to !== undefined) {
    return (
      <Link to={rest.to} ref={rest.ref} className={classes} aria-current={active ? 'page' : undefined}>
        {children}
      </Link>
    )
  }

  // `to` is undefined here, so spreading it adds no attribute.
  return <button type="button" {...rest} className={classes}>{children}</button>
}
